/**
 * The team page — design/PROTOTYPE-SPEC.md §12.
 *
 * Shows the seat count against the plan, pending invites with a resend action,
 * and role changes. Deactivation rather than deletion, because history keeps its
 * author.
 *
 * The permission checks here mirror the API's; they are a courtesy to the user,
 * not a control. Every one is enforced server-side, and hiding a button is never
 * what makes an action safe.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  JOB_TITLE_LABEL,
  JOB_TITLES,
  ROLES,
  ROLE_LABEL,
  can,
  type JobTitle as CoreJobTitle,
  type Role as CoreRole,
} from '@inventory-tracker/core';
import {
  ApiError,
  SeatLimitError,
  // NOTE: changeRole() exists in the client and is tested server-side, but the
  // team page has no role-editing control yet. Deliberate: changing someone's
  // permissions mid-slice without an audit-visible confirmation is worse than
  // making them re-invite. Add it with the settings page.
  deactivateMember,
  getTeam,
  inviteMember,
  reactivateMember,
  resendInvite,
  type Me,
  type TeamMember,
  type TeamResponse,
} from '../lib/api.js';
import { c, field, label, sans } from '../theme.js';
import { PinSettings } from './PinSettings.js';

export function TeamPage({ me }: { me: Me }) {
  const [data, setData] = useState<TeamResponse | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await getTeam());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the team');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const mayManage = can(me.user.role as CoreRole, 'manageTeam');

  if (!data) {
    return <p style={{ fontSize: 14, color: c.muted }}>{error || 'Loading…'}</p>;
  }

  const seatLabel =
    data.seats.limit === null
      ? `${data.seats.used} of unlimited`
      : `${data.seats.used} of ${data.seats.limit}`;

  return (
    <div data-tour="team">
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 6,
        }}
      >
        <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 28, margin: 0 }}>Team</h2>
        <span style={{ fontSize: 13, color: c.muted }}>
          {seatLabel} team {data.seats.limit === 1 ? 'login' : 'logins'} used
        </span>
      </div>
      <p style={{ fontSize: 13.5, color: c.muted, margin: '0 0 24px', lineHeight: 1.6 }}>
        Everyone signs in with their own account, so every movement records who made it.
      </p>

      {notice && (
        <div
          style={{
            background: c.okBg,
            border: '1px solid #D3DFCE',
            color: c.success,
            borderRadius: 12,
            padding: '11px 14px',
            fontSize: 13,
            marginBottom: 18,
          }}
        >
          {notice}
        </div>
      )}

      <div
        style={{
          background: c.card,
          border: `1px solid ${c.border}`,
          borderRadius: 18,
          overflow: 'hidden',
          marginBottom: 26,
        }}
      >
        {data.members.map((m, i) => (
          <MemberRow
            key={m.id}
            member={m}
            isSelf={m.id === me.user.id}
            viewerRole={me.user.role as CoreRole}
            mayManage={mayManage}
            last={i === data.members.length - 1}
            onChanged={(msg) => {
              setNotice(msg);
              void load();
            }}
            onError={setError}
          />
        ))}
      </div>

      {mayManage && (
        <InviteForm
          canInviteMore={data.seats.canInviteMore}
          viewerRole={me.user.role as CoreRole}
          onInvited={(msg) => {
            setNotice(msg);
            setError('');
            void load();
          }}
          onError={setError}
        />
      )}

      <PinSettings
        hasPin={data.members.find((m) => m.id === me.user.id)?.hasPin ?? false}
        onChanged={(msg) => {
          setNotice(msg);
          setError('');
          void load();
        }}
      />

      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            color: c.danger,
            borderRadius: 12,
            padding: '11px 14px',
            fontSize: 13,
            marginTop: 18,
            lineHeight: 1.5,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

function MemberRow({
  member,
  isSelf,
  viewerRole,
  mayManage,
  last,
  onChanged,
  onError,
}: {
  member: TeamMember;
  isSelf: boolean;
  viewerRole: CoreRole;
  mayManage: boolean;
  last: boolean;
  onChanged: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>, message: string) {
    setBusy(true);
    onError('');
    try {
      await action();
      onChanged(message);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  const deactivated = member.status === 'deactivated';
  // Only an owner may touch another owner — mirrors the API.
  const mayTouch = mayManage && !isSelf && (member.role !== 'owner' || viewerRole === 'owner');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '15px 20px',
        borderBottom: last ? 'none' : `1px solid ${c.border}`,
        opacity: deactivated ? 0.55 : 1,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14.5, fontWeight: 500 }}>{member.name}</span>
          {isSelf && <Pill tone="neutral">You</Pill>}
          {member.status === 'invited' && <Pill tone="pending">Invite pending</Pill>}
          {deactivated && <Pill tone="neutral">Deactivated</Pill>}
        </div>
        <div style={{ fontSize: 12.5, color: c.muted, marginTop: 3 }}>
          {member.email} · {JOB_TITLE_LABEL[member.jobTitle as CoreJobTitle]} ·{' '}
          {ROLE_LABEL[member.role as CoreRole]}
        </div>
      </div>

      {mayTouch && (
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {member.status === 'invited' && (
            <SmallButton
              disabled={busy}
              onClick={() =>
                run(() => resendInvite(member.id), `Invitation re-sent to ${member.email}`)
              }
            >
              Resend
            </SmallButton>
          )}
          {deactivated ? (
            <SmallButton
              disabled={busy}
              onClick={() => run(() => reactivateMember(member.id), `${member.name} reactivated`)}
            >
              Reactivate
            </SmallButton>
          ) : (
            <SmallButton
              disabled={busy}
              onClick={() =>
                run(() => deactivateMember(member.id), `${member.name} deactivated`)
              }
            >
              Deactivate
            </SmallButton>
          )}
        </div>
      )}
    </div>
  );
}

function InviteForm({
  canInviteMore,
  viewerRole,
  onInvited,
  onError,
}: {
  canInviteMore: boolean;
  viewerRole: CoreRole;
  onInvited: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState<CoreJobTitle>('injector');
  const [role, setRole] = useState<CoreRole>('staff');
  const [busy, setBusy] = useState(false);
  const [seatError, setSeatError] = useState('');

  // Only an owner can create another owner, so don't offer it otherwise.
  const offerableRoles = viewerRole === 'owner' ? ROLES : ROLES.filter((r) => r !== 'owner');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setSeatError('');
    onError('');
    try {
      await inviteMember({ name, email, role, jobTitle });
      onInvited(`Invitation sent to ${email}`);
      setName('');
      setEmail('');
    } catch (err) {
      if (err instanceof SeatLimitError) {
        // Distinct from a plain error: the answer is to upgrade.
        setSeatError(err.message);
      } else {
        onError(err instanceof ApiError ? err.message : 'Could not send that invitation');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      style={{
        background: c.card,
        border: `1px solid ${c.border}`,
        borderRadius: 18,
        padding: '20px 22px',
      }}
    >
      <h3 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 20, margin: '0 0 4px' }}>
        Invite someone
      </h3>
      <p style={{ fontSize: 12.5, color: c.muted, margin: '0 0 16px' }}>
        They’ll get an email with a link to set their own password. It lasts 7 days.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label htmlFor="inv-name" style={label}>
            Name
          </label>
          <input
            id="inv-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g. Nadia Rahman"
            style={field}
          />
        </div>
        <div>
          <label htmlFor="inv-email" style={label}>
            Email
          </label>
          <input
            id="inv-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="them@business.co.uk"
            style={field}
          />
        </div>
        <div>
          <label htmlFor="inv-title" style={label}>
            Job title
          </label>
          <select
            id="inv-title"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value as CoreJobTitle)}
            style={{ ...field, appearance: 'none' }}
          >
            {JOB_TITLES.map((t) => (
              <option key={t} value={t}>
                {JOB_TITLE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="inv-role" style={label}>
            Permissions
          </label>
          <select
            id="inv-role"
            value={role}
            onChange={(e) => setRole(e.target.value as CoreRole)}
            style={{ ...field, appearance: 'none' }}
          >
            {offerableRoles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p style={{ fontSize: 11.5, color: c.faint, margin: '0 0 14px', lineHeight: 1.6 }}>
        Job title is what your team sees. Permissions decide what they can do: managers
        can void movements, apply stock counts and manage the team; only owners can change
        the plan.
      </p>

      {seatError && (
        <div
          style={{
            background: c.amberBg,
            border: '1px solid #E8D7B4',
            color: c.amberText,
            borderRadius: 12,
            padding: '11px 14px',
            fontSize: 13,
            marginBottom: 14,
            lineHeight: 1.5,
          }}
        >
          {seatError}
        </div>
      )}

      <button
        type="submit"
        disabled={busy || !canInviteMore}
        style={{
          height: 44,
          padding: '0 20px',
          background: canInviteMore ? c.green : c.border,
          border: 'none',
          color: canInviteMore ? c.onDarkBright : c.muted,
          fontSize: 14,
          fontWeight: 500,
          fontFamily: sans,
          borderRadius: 980,
          cursor: busy || !canInviteMore ? 'not-allowed' : 'pointer',
          boxShadow: canInviteMore ? `0 1px 0 ${c.green} inset` : 'none',
        }}
      >
        {busy ? 'Sending…' : canInviteMore ? 'Send invitation' : 'No seats left on your plan'}
      </button>
    </form>
  );
}

function Pill({ tone, children }: { tone: 'neutral' | 'pending'; children: React.ReactNode }) {
  const tones = {
    neutral: { bg: c.well, border: c.borderStrong, fg: c.muted2 },
    pending: { bg: c.amberBg, border: '#D9C398', fg: c.amberText },
  } as const;
  const t = tones[tone];
  return (
    <span
      style={{
        fontSize: 10.5,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        fontWeight: 500,
        background: t.bg,
        border: `1px solid ${t.border}`,
        color: t.fg,
        borderRadius: 18,
        padding: '2px 7px',
      }}
    >
      {children}
    </span>
  );
}

function SmallButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'transparent',
        border: `1px solid ${c.borderStrong}`,
        color: c.muted2,
        fontSize: 12,
        fontWeight: 500,
        fontFamily: sans,
        padding: '7px 12px',
        borderRadius: 980,
        cursor: disabled ? 'progress' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}
